import React, {
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  FlatTree,
  // eslint-disable-next-line camelcase
  useHeadlessFlatTree_unstable,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
  useArrowNavigationGroup,
} from '@fluentui/react-components';
import { VirtualizerScrollViewDynamic } from '@fluentui/react-virtualizer';
import { useInfiniteScroll } from '../hooks/use-infinite-scroll';
import { FlatTreeItem } from './flat-tree-item';
import { SelectionFlatTreeProps } from '../types';

const useStyles = makeStyles({
  flatTree: {
    maxHeight: '50vh',
    overflowY: 'auto',
    ...shorthands.flex(1),

    '& > div': {
      ...shorthands.padding(tokens.strokeWidthThick),
      boxSizing: 'border-box',
    },

    '& > div::-webkit-scrollbar': {
      backgroundColor: 'transparent',
      width: tokens.spacingHorizontalS,
    },
    '& > div::-webkit-scrollbar-track': {
      ...shorthands.borderRadius(tokens.borderRadiusMedium),
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '& > div::-webkit-scrollbar-thumb': {
      ...shorthands.borderRadius(tokens.borderRadiusMedium),
      backgroundColor: tokens.colorNeutralForeground3,
    },
    '& > div::-webkit-scrollbar-thumb:hover': {
      backgroundColor: tokens.colorNeutralForeground3Hover,
    },
  },
});

const DefaultItemSize = 32;

export const SelectionFlatTree = ({
  itemsMap,
  openItems,
  onChange,
  onOpenChange,
  checkedItems,
  defaultCheckedItems,
  selectionMode,
  onRenderContent,
  onReachEnd,
  i18n,
  treeClassName,
  treeItems,
  ...restProps
}: SelectionFlatTreeProps) => {
  const classes = useStyles();

  const scrollViewRef = useRef<HTMLElement>(null);
  useInfiniteScroll(scrollViewRef, onReachEnd);

  const onSelectionChange = useCallback((event, data) => {
    if (checkedItems) {
      // If checkedItems is provided, it is a controlled component.
      onChange(data.checkedItems);
      return;
    }

    const selected = [] as any[];
    data.checkedItems.forEach((value, key) => {
      const item = itemsMap.get(key);
      if (item.allowedCheck && value) {
        selected.push(item.value);
      }
    });
    onChange(selected);
  }, [onChange, itemsMap]);

  const onOpenChangeInternal = useCallback((e, data) => {
    if (e.target.closest('.no-tree-open-close')) return;
    onOpenChange(data.openItems);
  }, [onOpenChange]);

  const flatTree = useHeadlessFlatTree_unstable(treeItems, {
    openItems,
    onOpenChange: onOpenChangeInternal,
    selectionMode: selectionMode === 'none' ? undefined : selectionMode,
    defaultCheckedItems,
    checkedItems: checkedItems !== undefined ? checkedItems : undefined,
    onCheckedChange: onSelectionChange,
  });

  const treeProps = useMemo(() => flatTree.getTreeProps(), [flatTree]);
  const flatTreeItems = useMemo(() => Array.from(flatTree.items()), [flatTree]);

  // Built in navigation does not work with virtualization.
  // Add arrow navigation.
  const attributes = useArrowNavigationGroup({ axis: 'vertical' });

  if (flatTreeItems.length === 0) {
    return (
      <div>
        {i18n.getString(_TL_('No results found'))}
      </div>
    );
  }

  return (
    <FlatTree
      className={mergeClasses('flat-tree', classes.flatTree, treeClassName)}
      {...treeProps}
      {...restProps}
      {...attributes}
    >
      <VirtualizerScrollViewDynamic
        scrollViewRef={scrollViewRef}
        numItems={flatTreeItems.length}
        itemSize={DefaultItemSize}
      >
        { (index) => {
          const flatTreeItem = flatTreeItems[index] as any;
          const isOpen = openItems.has(flatTreeItem.value);
          const isDisabled = itemsMap.get(flatTreeItem.value)?.disabled;

          return (
            <FlatTreeItem
              key={flatTreeItem.value}
              flatTreeItem={flatTreeItem}
              onRenderContent={onRenderContent}
              isOpen={isOpen}
              selectionMode={selectionMode}
              isDisabled={isDisabled}
            />
          );
        }}
      </VirtualizerScrollViewDynamic>
    </FlatTree>
  );
};
